import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CustomCard from '../../../Shared/Components/CustomCard/CustomCard';
import type { ReservationNoteDraft } from '../../Types/reservationTypes';

const MAX_NOTE_LENGTH = 2000;

export interface ReservationNotesSectionProps {
  notes: ReservationNoteDraft[] | undefined;
  onAddNote: (content: string) => void;
}

const ReservationNotesSection = ({ notes, onAddNote }: ReservationNotesSectionProps) => {
  const safeNotes = notes ?? [];
  const [modalOpen, setModalOpen] = useState(false);
  const [draftContent, setDraftContent] = useState('');
  const addButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!modalOpen) {
      setDraftContent('');
    }
  }, [modalOpen]);

  const trimmedContent = draftContent.trim();
  const isValid = trimmedContent.length > 0 && trimmedContent.length <= MAX_NOTE_LENGTH;

  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  const handleSave = () => {
    if (!isValid) {
      return;
    }
    onAddNote(trimmedContent);
    setModalOpen(false);
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="reservation-notes-section">
      <CustomCard
        title="Notatki"
        icon={<EditNoteIcon color="primary" />}
        action={
          <Button ref={addButtonRef} variant="outlined" size="small" onClick={handleOpen}>
            Dodaj notatkę
          </Button>
        }
      >
        {safeNotes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak notatek.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {safeNotes.map((note, index) => (
              <Box
                key={note.id ?? `draft-${index}`}
                data-testid="reservation-note-item"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  backgroundColor: 'background.paper',
                  transition: 'box-shadow 0.2s ease',
                  '&:hover': {
                    boxShadow: 1,
                  },
                }}
              >
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {note.content}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </CustomCard>

      <Dialog
        open={modalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="reservation-notes-dialog-title"
        slotProps={{
          transition: {
            onExited: () => addButtonRef.current?.focus(),
          },
        }}
      >
        <DialogTitle id="reservation-notes-dialog-title">Dodaj notatkę</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="Notatka"
            multiline
            minRows={4}
            fullWidth
            value={draftContent}
            onChange={(e) => setDraftContent(e.target.value)}
            inputProps={{ maxLength: MAX_NOTE_LENGTH }}
            helperText={`${trimmedContent.length}/${MAX_NOTE_LENGTH}`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Anuluj</Button>
          <Button variant="contained" onClick={handleSave} disabled={!isValid}>
            Zapisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReservationNotesSection;
